import React, { useContext, useState, useRef, useEffect } from 'react';
import { ThemeContext } from '../contexts/ThemeContext';
import { ResultsContext } from '../contexts/ResultsContext';
import './LinksCard.css';
import RankingDisclaimer from "./RankingDisclaimer";
import SternTournamentDisclaimer from "./SternTournamentDisclaimer";
import { QRCodeSVG } from 'qrcode.react';
import QrScanner from 'qr-scanner';
import * as XLSX from 'xlsx';
import { useTournamentAppConfiguration } from '../utils/tournamentconfig/TournamentConfiguration';

/**
 * LinksCard component displays a card with useful links
 * This component replicates the links and logic from the client app
 */
function AtGamesLinksCard({ tournamentId }: { tournamentId?: string } = {}) {
  const { theme } = useContext(ThemeContext);

  // Generate the tournament results link
  const tournamentResultsLink = tournamentId 
    ? `https://www.atgames.net/leaderboards/highscore/top50/${tournamentId}`
    : '#';
  
  return (
    <div className={`links-card ${theme}`}>
      <div className="links-card-content">
        <ul className="links-list">
          {tournamentId && (
            <li>
              <a 
                href={tournamentResultsLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Official Tournament Results {tournamentId && `(${tournamentId})`}
              </a>
            </li>
          )}
          <li>
            <a 
              href="https://www.atgames.net/leaderboards/schedule"
              target="_blank"
              rel="noopener noreferrer"
            >
              Upcoming Tournament Schedule
            </a>
          </li>
          <li>
            <a 
              href="https://www.ifpapinball.com/tournaments/find.php?search=&country=&stateprov=&ranktype=VIRTUAL&event_type=&start_date=2021-01-01&end_date=2035-12-31&min_points=&max_points="
              target="_blank"
              rel="noopener noreferrer"
            >
              IFPA Tournament Results
            </a>
          </li>
          <li>
            <a
                href="https://www.ifpapinball.com/ifpa-atgames-virtual-pinball-tournaments/"
                target="_blank"
                rel="noopener noreferrer"
            >
              IFPA AtGames Virtual Pinball Tournaments Rules
            </a>
          </li>
          <li>
            <a 
              href="mailto:FliPal@mikedg.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact Support
            </a>
          </li>
          <RankingDisclaimer />
        </ul>
      </div>
    </div>
  );
}

function SternLinksCard({ tournamentId }: { tournamentId: string }) {
  const { theme } = useContext(ThemeContext);
  const resultsContext = useContext(ResultsContext);
  const results = resultsContext?.results;
  const config = useTournamentAppConfiguration();

  const [showScanner, setShowScanner] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<QrScanner | null>(null);

  // Generate the tournament results link
  const tournamentResultsLink = tournamentId
      ? `https://insider.sternpinball.com/ic/leaderboards/${tournamentId}`
      : '#';

  // Generate XLSX download function
  const downloadXLSX = () => {
    if (!results || !results.games) {
      alert('No tournament data available for XLSX export');
      return;
    }

    // Get all unique players
    const players = new Set<string>();
    results.games.forEach((game: any) => {
      game.leaderboard.forEach((entry: any) => players.add(entry.userName));
    });

    // Build header
    const header = ['rank by sort', 'name'];
    results.games.forEach((game: any) => {
      header.push(`${game.title} name`, `${game.title} rank`, `${game.title} score`);
    });
    header.push('points (formula)', 'points (web calc_');

    // Build rows
    const dataRows: any[] = [];
    players.forEach(player => {
      const row: any[] = []
      row.push(''); // Placeholder for rank formula
      row.push(player); //player name
      
      results.games.forEach((game: any) => {
        const entry = game.leaderboard.find((e: any) => e.userName === player);
        if (entry) {
          row.push(game.title, entry.rank, entry.score);
        } else {
          row.push(game.title, '', '');
        }
      });
      
      row.push(''); // Placeholder for points formula
      
      // Add second points column with dropNLowest calculation
      const playerRanks = results.games.map((game: any) => {
        const entry = game.leaderboard.find((e: any) => e.userName === player);
        return entry ? entry.rank : null;
      }).filter((rank: any) => rank !== null);
      
      let points2 = 1000000;
      if (playerRanks.length >= Math.max(1, results.games.length - config.dropNLowest)) {
        const sortedRanks = [...playerRanks].sort((a, b) => a - b);
        const ranksToSum = sortedRanks.slice(0, Math.max(1, results.games.length - config.dropNLowest));
        points2 = ranksToSum.reduce((sum, rank) => sum + rank, 0);
      }
      row.push(points2);
      
      dataRows.push(row);
    });

    // Sort rows by points (web calc) column (last column)
    dataRows.sort((a, b) => a[a.length - 1] - b[b.length - 1]);

    // Add sorted rows to final rows array with formulas calculated after sorting
    const rows = [header];
    dataRows.forEach((row, index) => {
      const rowIndex = index + 2; // Excel rows start at 1, header is row 1, data starts at row 2
      
      // Update the rank formula to use actual row number
      row[0] = {t: 'f', f: `${index + 1}`};
      
      // Calculate rank columns for formula (every 3rd column starting from D)
      const rankColumns: string[] = [];
      results.games.forEach((game: any, gameIndex: number) => {
        const rankColLetter = String.fromCharCode(68 + gameIndex * 3); // D, G, J, M, etc.
        rankColumns.push(`${rankColLetter}${rowIndex}`);
      });
      
      // Add points formula
      const rankRefs = rankColumns.join(',');
      // From Ed: IF(COUNTA(D2,G2,J2,M2)=4,SUM(D2,G2,J2,M2)−MAX(D2,G2,J2,M2),SUM(D2,G2,J2,M2))
      // Take tournament, drop N, rankColums.count - dropN
      // const columnsForRank = rankColumns.length - config.dropNLowest
      // Ed formula
      // TODO: we need MAX X
      //  WRORKING =IF(COUNTA(A2:G2) = 3,SUM(A2:G2),SUM(A2:G2) - SUM(LARGE(A2:G2, SEQUENCE($O$2 - COUNTBLANK(A2:G2)))))
      const nToDrop = config.dropNLowest
      const howManyWeWant = rankColumns.length - nToDrop

      // Bad countblank because disjoint range
      //  const formula =`IF(COUNTA(${rankRefs}) = ${howManyWeWant},SUM(${rankRefs}),SUM(${rankRefs}) - SUM(LARGE({${rankRefs}}, SEQUENCE(${nToDrop} - COUNTBLANK({${rankRefs}})))))`

      // my own countblank, must use strings!
      const stringColumns = rankColumns.map(col => `"${col}"`).join(',');
      const customCountBlankForDisjointRanges = `SUM(COUNTIF(INDIRECT({${stringColumns}}),""))`

       const formula =`IF(COUNTA(${rankRefs}) = ${howManyWeWant},SUM(${rankRefs}),SUM(${rankRefs}) - SUM(LARGE((${rankRefs}), SEQUENCE(${nToDrop} - ${customCountBlankForDisjointRanges})))`
      //TODO: N TO DROP is 0....
       console.log(formula)
      // const formula = `IF(COUNTA(${rankRefs})=4,SUM(${rankRefs})−MAX(${rankRefs}),SUM(${rankRefs}))`
      // GPT - broken formula
      // const formula = `IF(COUNTA(${rankRefs})<${columnsForRank}, 1000000, SUM(SMALL(IF(ISNUMBER({${rankRefs}}), {${rankRefs}}), ${columnsForRank})))`;
      // countblank doesnt work across disjoint ranges
      // row[row.length - 2] = {t: 'f', f: formula}; // Second to last column
      row[row.length - 2] = 0

      rows.push(row);
    });

    // Generate filename: "event code - tournament name - date - time.xlsx"
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      }).replace(/\//g, '-');
    };

    const formatTime = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false
      }).replace(/:/g, '-');
    };

    const eventCode = results.tournamentId || 'unknown';
    const tournamentName = results.title || 'Tournament';
    const date = results.start ? formatDate(results.start) : 'unknown-date';
    const time = results.start ? formatTime(results.start) : 'unknown-time';
    
    const filename = `${eventCode} - ${tournamentName} - ${date} - ${time}.xlsx`;

    // Create config sheet data
    const configRows = [['Key', 'Value']];
    const addConfigRows = (obj: any, prefix = '') => {
      Object.entries(obj).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null && !React.isValidElement(value)) {
          configRows.push([prefix + key, '']);
          addConfigRows(value, prefix + '  ');
        } else {
          configRows.push([prefix + key, typeof value === 'function' ? '[Function]' : String(value)]);
        }
      });
    };
    addConfigRows(config);

    // Create and download the XLSX file
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const configSheet = XLSX.utils.aoa_to_sheet(configRows);
    
    // Freeze the first row as header
    worksheet['!freeze'] = { xSplit: 0, ySplit: 1 }; // Not sure if this is working

    // Doesn't seem to work, but gonna commit anyway
    // Highlight top n players in yellow based on cutoffRank
    const yellowFill = { fgColor: { rgb: "FFFF00" } };
    for (let i = 2; i <= Math.min(config.cutoffRank + 1, rows.length); i++) {
      const range = XLSX.utils.encode_range({ s: { c: 0, r: i - 1 }, e: { c: header.length - 1, r: i - 1 } });
      const cells = XLSX.utils.decode_range(range);
      for (let col = cells.s.c; col <= cells.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ c: col, r: i - 1 });
        if (!worksheet[cellAddress]) worksheet[cellAddress] = {};
        worksheet[cellAddress].s = { fill: yellowFill };
      }
    }
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Tournament Results');
    XLSX.utils.book_append_sheet(workbook, configSheet, 'Configuration');
    XLSX.writeFile(workbook, filename);
  };

  useEffect(() => {
    if (showScanner && videoRef.current) {
      const startScanner = async () => {
        try {
          await navigator.mediaDevices.getUserMedia({ video: true });
          scannerRef.current = new QrScanner(
            videoRef.current!,
            (result) => {
              console.log('Tournament ID from QR code:', result.data);
              setShowScanner(false);
              if (config.qrScan?.processor) {
                config.qrScan.processor(result.data);
              }
            },
            {
              highlightScanRegion: true,
              highlightCodeOutline: true,
            }
          );
          await scannerRef.current.start();
        } catch (error) {
          console.error('Camera error:', error);
          alert('Camera access denied or not available. Please allow camera permissions.');
          setShowScanner(false);
        }
      };
      startScanner();
    }
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop();
        scannerRef.current.destroy();
      }
    };
  }, [showScanner, config.qrScan]);

  return (
      <div className={`links-card ${theme}`}>
        <div className="links-card-content">
          <ul className="links-list">
            {tournamentId && (
                <>
                <li>
                  <a
                      href={tournamentResultsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                  >
                    Official Tournament Results {tournamentId && `(${tournamentId})`}
                  </a>
                </li>
                <li>
                  Share this Tournament Link<br/>
                    <QRCodeSVG value={window.location.href} size={120}/>
                </li>
                </>
            )}
            <li>
              <button 
                onClick={() => setShowScanner(true)}
                style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
              >
                Scan Tournament QR Code
              </button>
            </li>
            <li>
              <button 
                onClick={downloadXLSX}
                style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
              >
                Download Tournament XLSX
              </button>
            </li>
            <li>
              <a
                  href="mailto:FliPal@mikedg.com"
                  target="_blank"
                  rel="noopener noreferrer"
              >
                Contact Support
              </a>
            </li>
            <SternTournamentDisclaimer />
          </ul>
        </div>
        {showScanner && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: theme === 'dark' ? '#333' : 'white',
              color: theme === 'dark' ? '#f5f5f5' : '#333',
              padding: '20px',
              borderRadius: '8px',
              position: 'relative'
            }}>
              <button 
                onClick={() => setShowScanner(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
              <h3>Scan Tournament QR Code</h3>
              <video ref={videoRef} style={{ width: '300px', height: '300px' }} />
            </div>
          </div>
        )}
      </div>
  );
}

export { AtGamesLinksCard, SternLinksCard };